import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBan,
  FaBold,
  FaCheck,
  FaDownload,
  FaEraser,
  FaFileMedical,
  FaFont,
  FaHighlighter,
  FaICursor,
  FaImage,
  FaItalic,
  FaMousePointer,
  FaPen,
  FaPlus,
  FaRedo,
  FaSearchMinus,
  FaSearchPlus,
  FaSignature,
  FaSquare,
  FaStamp,
  FaStickyNote,
  FaTimes,
  FaTrash,
  FaUnderline,
  FaUndo,
  FaUpload,
  FaWater,
  FaFileAlt,
  FaClone,
  FaObjectGroup,
} from "react-icons/fa";
import { useLanguage } from "../../../hooks/LanguageContext";
import {
  PAGE_SIZES,
  createBlankPdf,
  exportAnnotatedPdf,
  mergePdfFiles,
  addWatermarkToBytes,
  downloadBytes,
  dataUrlToBytes,
  insertBlankPage,
  duplicatePageInPdf,
} from "./pdfExport";
import { extractEditableLines, groupLinesIntoBlocks } from "./pdfTextUtils";
import "../../../styles/components/PdfEditor.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const TOOL = {
  SELECT: "select",
  TEXT: "text",
  EDIT: "edit",
  HIGHLIGHT: "highlight",
  DRAW: "draw",
  RECT: "rect",
  NOTE: "note",
  IMAGE: "image",
  SIGN: "sign",
  STAMP: "stamp",
  REDACT: "redact",
  ERASE: "erase",
};

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const COLORS = ["#111111", "#c0392b", "#2980b9", "#27ae60", "#f39c12", "#8e44ad", "#ffffff"];
const SIG_STORAGE_KEY = "freepdf-default-signature";

function loadSavedSignature() {
  try {
    return localStorage.getItem(SIG_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function persistSavedSignature(dataUrl) {
  try {
    if (dataUrl) localStorage.setItem(SIG_STORAGE_KEY, dataUrl);
    else localStorage.removeItem(SIG_STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

function PdfEditor() {
  const { isEnglish } = useLanguage();
  const t = useCallback((en, es) => (isEnglish ? en : es), [isEnglish]);

  const [pdfBytes, setPdfBytes] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [fileName, setFileName] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1.35);
  const [tool, setTool] = useState(TOOL.TEXT);
  const [anns, setAnns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([[]]);
  const [histIdx, setHistIdx] = useState(0);
  const [thumbs, setThumbs] = useState([]);
  const [textLayer, setTextLayer] = useState([]);
  const [textLines, setTextLines] = useState([]);
  const [editGrain, setEditGrain] = useState("line"); // line | block
  const [hoverTextId, setHoverTextId] = useState(null);
  const [busy, setBusy] = useState(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const [toast, setToast] = useState("");
  const [dragOver, setDragOver] = useState(false);

  // format
  const [color, setColor] = useState("#111111");
  const [fontSize, setFontSize] = useState(18);
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [fontFamily, setFontFamily] = useState("helvetica");
  const [strokeWidth, setStrokeWidth] = useState(3);

  // inline editors
  const [draftText, setDraftText] = useState(null); // {x,y,w,h,value,mode,native?}
  const [showCreate, setShowCreate] = useState(false);
  const [showSign, setShowSign] = useState(false);
  const [savedSignature, setSavedSignature] = useState(() => loadSavedSignature());
  const [saveSigAsDefault, setSaveSigAsDefault] = useState(true);
  const [forceNewSignature, setForceNewSignature] = useState(false);
  const [showStamp, setShowStamp] = useState(false);
  const [showWater, setShowWater] = useState(false);
  const [createSize, setCreateSize] = useState("a4");
  const [createTpl, setCreateTpl] = useState("blank");
  const [stampLabel, setStampLabel] = useState("APROBADO");
  const [waterText, setWaterText] = useState("CONFIDENCIAL");
  const [pageCss, setPageCss] = useState({ w: 0, h: 0 });
  const [hint, setHint] = useState("");

  const canvasRef = useRef(null);
  const drawRef = useRef(null);
  const stageRef = useRef(null);
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const mergeRef = useRef(null);
  const drawing = useRef(null);
  const sigRef = useRef(null);
  const sigDraw = useRef(false);
  const annsRef = useRef(anns);
  annsRef.current = anns;
  const renderToken = useRef(0);

  const selected = useMemo(() => anns.find((a) => a.id === selectedId) || null, [anns, selectedId]);

  const notify = (msg) => {
    setToast(msg);
    clearTimeout(notify._t);
    notify._t = setTimeout(() => setToast(""), 2400);
  };

  const commit = useCallback(
    (next) => {
      setAnns(next);
      setHistory((h) => [...h.slice(0, histIdx + 1), next]);
      setHistIdx((i) => i + 1);
    },
    [histIdx]
  );

  const undo = () => {
    if (histIdx <= 0) return;
    const i = histIdx - 1;
    setHistIdx(i);
    setAnns(history[i]);
  };
  const redo = () => {
    if (histIdx >= history.length - 1) return;
    const i = histIdx + 1;
    setHistIdx(i);
    setAnns(history[i]);
  };

  const updateSelected = (patch) => {
    if (!selectedId) return;
    commit(anns.map((a) => (a.id === selectedId ? { ...a, ...patch } : a)));
  };

  /* ---------- load PDF ---------- */
  const openBytes = async (bytes, name) => {
    setBusy(true);
    try {
      const doc = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
      setPdfDoc(doc);
      setPdfBytes(bytes);
      setNumPages(doc.numPages);
      setPage(1);
      setAnns([]);
      setHistory([[]]);
      setHistIdx(0);
      setSelectedId(null);
      setDraftText(null);
      setFileName(name || "documento.pdf");
      setTool(TOOL.TEXT);
      await makeThumbs(doc);
      notify(t("PDF loaded", "PDF cargado"));
      setHint(t("Click anywhere to add text", "Haz clic donde quieras añadir texto"));
    } catch (e) {
      console.error(e);
      notify(t("Could not open PDF", "No se pudo abrir el PDF"));
    } finally {
      setBusy(false);
    }
  };

  const openFile = async (file) => {
    if (!file || file.type !== "application/pdf") {
      notify(t("Please choose a PDF", "Elige un archivo PDF"));
      return;
    }
    const buf = await file.arrayBuffer();
    await openBytes(new Uint8Array(buf), file.name);
  };

  const makeThumbs = async (doc) => {
    const out = [];
    const n = Math.min(doc.numPages, 40);
    for (let i = 1; i <= n; i++) {
      const p = await doc.getPage(i);
      const vp = p.getViewport({ scale: 0.2 });
      const c = document.createElement("canvas");
      c.width = vp.width;
      c.height = vp.height;
      await p.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
      out.push(c.toDataURL("image/jpeg", 0.75));
    }
    setThumbs(out);
  };

  const createNew = async () => {
    setBusy(true);
    try {
      const bytes = await createBlankPdf(createSize, createTpl, {});
      await openBytes(bytes, `nuevo-${createTpl}.pdf`);
      setShowCreate(false);
    } catch (e) {
      console.error(e);
      notify(t("Create failed", "Error al crear"));
    } finally {
      setBusy(false);
    }
  };

  /* ---------- high-quality page render ---------- */
  useEffect(() => {
    if (!pdfDoc) return undefined;
    let cancelled = false;
    const token = ++renderToken.current;

    (async () => {
      const pdfPage = await pdfDoc.getPage(page);
      if (cancelled || token !== renderToken.current) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      const viewport = pdfPage.getViewport({ scale: zoom * dpr });
      const cssW = viewport.width / dpr;
      const cssH = viewport.height / dpr;

      const canvas = canvasRef.current;
      const draw = drawRef.current;
      if (!canvas || !draw) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      draw.width = viewport.width;
      draw.height = viewport.height;
      draw.style.width = `${cssW}px`;
      draw.style.height = `${cssH}px`;
      setPageCss({ w: cssW, h: cssH });

      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await pdfPage.render({
        canvasContext: ctx,
        viewport,
        intent: "display",
      }).promise;

      if (cancelled || token !== renderToken.current) return;

      // Geometry must use the same CSS viewport as the visible page (zoom),
      // not the DPR-scaled canvas — otherwise hit boxes drift.
      const cssViewport = pdfPage.getViewport({ scale: zoom });
      const lines = await extractEditableLines(pdfPage, cssViewport);
      if (cancelled || token !== renderToken.current) return;
      setTextLines(lines);
      setTextLayer(groupLinesIntoBlocks(lines));
      paintDrawLayer();
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, page, zoom]);

  useEffect(() => {
    paintDrawLayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anns, selectedId, page, pageCss]);

  useEffect(() => {
    const hints = {
      [TOOL.TEXT]: t("Click on the page to place text", "Haz clic en la página para colocar texto"),
      [TOOL.EDIT]: t(
        "Blue boxes = detected text. Click a line to edit it. Switch to “Paragraph” for multi-line blocks.",
        "Las cajas azules son el texto detectado. Haz clic en una línea para editarla. Usa “Párrafo” para bloques."
      ),
      [TOOL.HIGHLIGHT]: t("Drag to highlight", "Arrastra para resaltar"),
      [TOOL.DRAW]: t("Draw freely on the page", "Dibuja libremente en la página"),
      [TOOL.RECT]: t("Drag to draw a rectangle", "Arrastra para dibujar un rectángulo"),
      [TOOL.NOTE]: t("Click to add a sticky note", "Haz clic para añadir una nota"),
      [TOOL.IMAGE]: t("Click where to place an image", "Haz clic donde colocar una imagen"),
      [TOOL.SIGN]: savedSignature
        ? t("Click to place your saved signature", "Haz clic para colocar tu firma guardada")
        : t("Click where to place your signature", "Haz clic donde colocar la firma"),
      [TOOL.STAMP]: t("Click where to place a stamp", "Haz clic donde colocar un sello"),
      [TOOL.REDACT]: t("Drag to black out content", "Arrastra para tapar en negro"),
      [TOOL.ERASE]: t("Click an object to delete it", "Haz clic en un objeto para borrarlo"),
      [TOOL.SELECT]: t("Click an object to select it", "Haz clic en un objeto para seleccionarlo"),
    };
    setHint(hints[tool] || "");
  }, [tool, t, savedSignature]);

  const paintDrawLayer = () => {
    const canvas = drawRef.current;
    if (!canvas || !pageCss.w) return;
    const ctx = canvas.getContext("2d");
    const dpr = canvas.width / pageCss.w;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const W = pageCss.w;
    const H = pageCss.h;
    anns
      .filter((a) => a.pageIndex === page - 1)
      .forEach((a) => drawAnn(ctx, a, W, H, a.id === selectedId));

    if (drawing.current) drawAnn(ctx, drawing.current, W, H, false);
  };

  const drawAnn = (ctx, a, W, H, sel) => {
    const x = a.x * W;
    const y = a.y * H;
    const w = (a.w || 0) * W;
    const h = (a.h || 0) * H;
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = a.color || "#c0392b";
    ctx.fillStyle = a.color || "#c0392b";
    ctx.lineWidth = a.strokeWidth || 2;
    ctx.globalAlpha = a.opacity ?? 1;

    if (a.type === "highlight") {
      ctx.globalAlpha = a.opacity ?? 0.35;
      ctx.fillRect(x, y, w, Math.max(h, 14));
    } else if (a.type === "rect" || a.type === "redact") {
      if (a.type === "redact") {
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#000";
        ctx.fillRect(x, y, w, h);
      } else {
        ctx.strokeRect(x, y, w, h);
      }
    } else if (a.type === "draw") {
      const pts = a.points || [];
      if (pts.length > 1) {
        ctx.beginPath();
        ctx.moveTo(pts[0].x * W, pts[0].y * H);
        pts.slice(1).forEach((p) => ctx.lineTo(p.x * W, p.y * H));
        ctx.stroke();
      }
    } else if (a.type === "image" || a.type === "signature") {
      if (a.imgEl) ctx.drawImage(a.imgEl, x, y, w, h);
      else ctx.strokeRect(x, y, w, h);
    } else if (a.type === "stamp") {
      ctx.font = `bold ${a.fontSize || 18}px Syne, sans-serif`;
      const label = a.text || "APROBADO";
      const tw = ctx.measureText(label).width;
      ctx.lineWidth = 2.5;
      ctx.strokeRect(x, y, tw + 18, (a.fontSize || 18) + 14);
      ctx.fillText(label, x + 9, y + (a.fontSize || 18) + 2);
    } else if (a.type === "note") {
      ctx.globalAlpha = 0.95;
      ctx.fillStyle = "#ffe566";
      ctx.fillRect(x, y, w || 150, h || 80);
      ctx.strokeStyle = "#d4a017";
      ctx.strokeRect(x, y, w || 150, h || 80);
      ctx.fillStyle = "#333";
      ctx.font = "12px Manrope, sans-serif";
      wrapText(ctx, a.text || "", x + 8, y + 18, (w || 150) - 16, 14);
    }
    // text / textReplace rendered as HTML for clarity

    if (sel) {
      ctx.globalAlpha = 1;
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = "#3a8fc4";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x - 3, y - 3, Math.max(w, 20) + 6, Math.max(h, 16) + 6);
    }
    ctx.restore();
  };

  const normFromEvent = (e) => {
    const stage = stageRef.current;
    const rect = stage.getBoundingClientRect();
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    };
  };

  const hitAnn = (nx, ny) => {
    const list = anns.filter((a) => a.pageIndex === page - 1);
    for (let i = list.length - 1; i >= 0; i--) {
      const a = list[i];
      if (a.type === "draw") {
        if ((a.points || []).some((p) => Math.hypot(p.x - nx, p.y - ny) < 0.018)) return a;
        continue;
      }
      const w = a.w || 0.05;
      const h = a.h || 0.03;
      if (nx >= a.x && nx <= a.x + w && ny >= a.y && ny <= a.y + h) return a;
    }
    return null;
  };

  const openNativeEdit = (native) => {
    const cssSize = native.fontSizeCss || fontSize;
    setDraftText({
      mode: "edit",
      x: native.x,
      y: native.y,
      w: Math.max(native.w, 0.08),
      h: Math.max(native.h, 0.022),
      value: native.str,
      fontSizeCss: cssSize,
      // PDF points for export
      fontSize: Math.max(8, cssSize / zoomRef.current),
      nativeId: native.id,
      lineCount: native.lineCount || 1,
    });
    setColor("#111111");
    setBold(false);
    setItalic(false);
    setUnderline(false);
  };

  /* ---------- pointer interactions ---------- */
  const onPointerDown = (e) => {
    if (!pdfDoc || draftText) return;
    // ignore if clicking UI controls inside stage
    if (e.target.closest(".pe2-text-box, .pe2-draft, .pe2-native-hit, .pe2-edit-toolbar")) return;

    const pos = normFromEvent(e);
    const pageIndex = page - 1;

    if (tool === TOOL.SELECT) {
      const hit = hitAnn(pos.x, pos.y);
      setSelectedId(hit?.id || null);
      if (hit && (hit.type === "text" || hit.type === "textReplace")) {
        setColor(hit.color || "#111");
        setFontSize(hit.fontSize || 18);
        setBold(!!hit.bold);
        setItalic(!!hit.italic);
        setUnderline(!!hit.underline);
        setFontFamily(hit.fontFamily || "helvetica");
      }
      return;
    }

    if (tool === TOOL.ERASE) {
      const hit = hitAnn(pos.x, pos.y);
      if (hit) {
        commit(anns.filter((a) => a.id !== hit.id));
        setSelectedId(null);
      }
      return;
    }

    if (tool === TOOL.EDIT) {
      const regions = editGrain === "block" ? textLayer : textLines;
      const native =
        regions.find(
          (it) => nxIn(pos.x, it.x, it.w) && nxIn(pos.y, it.y, it.h)
        ) || null;
      if (!native) return;
      openNativeEdit(native);
      return;
    }

    if (tool === TOOL.TEXT) {
      setDraftText({
        mode: "add",
        x: pos.x,
        y: pos.y,
        w: 0.35,
        h: 0.05,
        value: "",
        fontSize,
      });
      return;
    }

    if (tool === TOOL.NOTE) {
      setDraftText({
        mode: "note",
        x: pos.x,
        y: pos.y,
        w: 0.26,
        h: 0.12,
        value: "",
        fontSize: 13,
      });
      return;
    }

    if (tool === TOOL.IMAGE) {
      drawing.current = { pendingImage: true, x: pos.x, y: pos.y, pageIndex };
      imgRef.current?.click();
      return;
    }
    if (tool === TOOL.SIGN) {
      drawing.current = { pendingSign: true, x: pos.x, y: pos.y, pageIndex };
      if (savedSignature && !forceNewSignature) {
        placeSavedSignature(pos.x, pos.y, pageIndex, savedSignature);
      } else {
        setShowSign(true);
      }
      return;
    }
    if (tool === TOOL.STAMP) {
      drawing.current = { pendingStamp: true, x: pos.x, y: pos.y, pageIndex };
      setShowStamp(true);
      return;
    }

    if ([TOOL.HIGHLIGHT, TOOL.DRAW, TOOL.RECT, TOOL.REDACT].includes(tool)) {
      drawing.current = {
        id: uid(),
        type: tool === TOOL.HIGHLIGHT ? "highlight" : tool === TOOL.REDACT ? "redact" : tool === TOOL.RECT ? "rect" : "draw",
        pageIndex,
        x: pos.x,
        y: pos.y,
        w: 0,
        h: 0,
        color: tool === TOOL.HIGHLIGHT ? "#f1c40f" : color,
        opacity: tool === TOOL.HIGHLIGHT ? 0.4 : 1,
        strokeWidth,
        points: tool === TOOL.DRAW ? [{ x: pos.x, y: pos.y }] : undefined,
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
  };

  const onPointerMove = (e) => {
    const d = drawing.current;
    if (!d || d.pendingImage || d.pendingSign || d.pendingStamp) return;
    const pos = normFromEvent(e);
    if (d.type === "draw") {
      d.points = [...(d.points || []), pos];
    } else {
      d.w = pos.x - d.x;
      d.h = pos.y - d.y;
      if (d.type === "highlight") d.h = Math.max(Math.abs(d.h), 0.02);
    }
    paintDrawLayer();
  };

  const onPointerUp = () => {
    const d = drawing.current;
    if (!d || d.pendingImage || d.pendingSign || d.pendingStamp) return;
    let final = { ...d };
    if (final.w < 0) {
      final.x += final.w;
      final.w = Math.abs(final.w);
    }
    if (final.h < 0) {
      final.y += final.h;
      final.h = Math.abs(final.h);
    }
    const bad =
      final.type !== "draw" && final.w < 0.008 && final.h < 0.008;
    const badDraw = final.type === "draw" && (final.points || []).length < 2;
    drawing.current = null;
    if (bad || badDraw) {
      paintDrawLayer();
      return;
    }
    commit([...annsRef.current, final]);
  };

  const confirmDraft = () => {
    if (!draftText) return;
    const value = (draftText.value || "").trim();
    if (!value) {
      setDraftText(null);
      return;
    }

    if (draftText.mode === "edit-ann" && draftText.annId) {
      commit(
        anns.map((a) =>
          a.id === draftText.annId
            ? {
                ...a,
                text: value,
                color,
                fontSize: draftText.fontSize || fontSize,
                fontFamily,
                bold,
                italic,
                underline,
              }
            : a
        )
      );
      notify(t("Text updated", "Texto actualizado"));
    } else if (draftText.mode === "edit") {
      const pdfFontSize = Math.max(
        8,
        draftText.fontSize || (draftText.fontSizeCss || fontSize) / zoomRef.current
      );
      // Slightly expand cover so original glyphs disappear cleanly
      const padX = 0.004;
      const padY = 0.002;
      commit([
        ...anns,
        {
          id: uid(),
          type: "textReplace",
          pageIndex: page - 1,
          x: Math.max(0, draftText.x - padX),
          y: Math.max(0, draftText.y - padY),
          w: Math.min(1, Math.max(draftText.w, 0.06) + padX * 2),
          h: Math.min(1, Math.max(draftText.h, 0.02) + padY * 2),
          text: value,
          color,
          fontSize: pdfFontSize,
          fontSizeAbsolute: true,
          fontFamily,
          bold,
          italic,
          underline,
          cover: true,
          coverColor: "#ffffff",
          lineHeight: 1.25,
        },
      ]);
      notify(t("Text updated", "Texto actualizado"));
    } else if (draftText.mode === "note") {
      commit([
        ...anns,
        {
          id: uid(),
          type: "note",
          pageIndex: page - 1,
          x: draftText.x,
          y: draftText.y,
          w: 0.26,
          h: 0.12,
          text: value,
          color: "#f1c40f",
        },
      ]);
    } else {
      const lines = value.split("\n").length;
      commit([
        ...anns,
        {
          id: uid(),
          type: "text",
          pageIndex: page - 1,
          x: draftText.x,
          y: draftText.y,
          w: Math.max(0.2, Math.min(0.7, value.length * 0.012)),
          h: Math.max(0.035, lines * (fontSize / pageCss.h) * 1.35),
          text: value,
          color,
          fontSize,
          fontFamily,
          bold,
          italic,
          underline,
          align: "left",
        },
      ]);
      notify(t("Text added", "Texto añadido"));
    }
    setDraftText(null);
  };

  const onImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const d = drawing.current;
    if (!file || !d?.pendingImage) {
      drawing.current = null;
      return;
    }
    const dataUrl = await readAsDataURL(file);
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const bytes = await dataUrlToBytes(dataUrl);
    const size = fitImageOnPage(img.width, img.height, pageCss.w, pageCss.h, 0.32, 0.4);
    commit([
      ...anns,
      {
        id: uid(),
        type: "image",
        pageIndex: d.pageIndex,
        x: d.x,
        y: d.y,
        w: size.w,
        h: size.h,
        imageDataUrl: dataUrl,
        imageBytes: bytes,
        imageMime: file.type,
        imgEl: img,
      },
    ]);
    drawing.current = null;
  };

  const placeSavedSignature = async (x, y, pageIndex, dataUrl) => {
    try {
      // Normalize old/new signatures: trim canvas padding and keep real aspect
      const cleanUrl = await normalizeSignatureDataUrl(dataUrl);
      const img = new Image();
      img.src = cleanUrl;
      await img.decode();
      const size = fitImageOnPage(
        img.width,
        img.height,
        pageCss.w || 595,
        pageCss.h || 842,
        0.34,
        0.14
      );
      commit([
        ...annsRef.current,
        {
          id: uid(),
          type: "signature",
          pageIndex,
          x,
          y,
          w: size.w,
          h: size.h,
          imageDataUrl: cleanUrl,
          imageBytes: await dataUrlToBytes(cleanUrl),
          imageMime: "image/png",
          imgEl: img,
        },
      ]);
      drawing.current = null;
      setForceNewSignature(false);
      notify(t("Signature placed", "Firma colocada"));
    } catch (err) {
      console.error(err);
      setShowSign(true);
    }
  };

  const placeSign = async () => {
    const d = drawing.current;
    const c = sigRef.current;
    if (!d?.pendingSign || !c) {
      setShowSign(false);
      return;
    }
    const dataUrl = cropSignatureToInk(c);
    if (saveSigAsDefault) {
      persistSavedSignature(dataUrl);
      setSavedSignature(dataUrl);
    }
    await placeSavedSignature(d.x, d.y, d.pageIndex, dataUrl);
    setShowSign(false);
  };

  const clearSavedSignature = () => {
    persistSavedSignature("");
    setSavedSignature("");
    setForceNewSignature(true);
    notify(t("Saved signature removed", "Firma guardada eliminada"));
  };

  const placeStamp = () => {
    const d = drawing.current;
    if (!d?.pendingStamp) {
      setShowStamp(false);
      return;
    }
    commit([
      ...anns,
      {
        id: uid(),
        type: "stamp",
        pageIndex: d.pageIndex,
        x: d.x,
        y: d.y,
        w: 0.22,
        h: 0.05,
        text: stampLabel,
        color,
        fontSize: 18,
      },
    ]);
    drawing.current = null;
    setShowStamp(false);
  };

  const initSig = async (preload) => {
    const c = sigRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    const src = preload || (forceNewSignature ? "" : savedSignature);
    if (src) {
      try {
        const img = new Image();
        img.src = src;
        await img.decode();
        const pad = 8;
        const scale = Math.min((c.width - pad * 2) / img.width, (c.height - pad * 2) / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        ctx.drawImage(img, (c.width - dw) / 2, (c.height - dh) / 2, dw, dh);
      } catch {
        /* blank canvas */
      }
    }
  };

  useEffect(() => {
    if (showSign) requestAnimationFrame(() => initSig());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSign]);

  /* ---------- export / page ops ---------- */
  const download = async () => {
    if (!pdfBytes) return;
    setBusy(true);
    try {
      const ready = [];
      for (const a of anns) {
        if ((a.type === "image" || a.type === "signature") && a.imageDataUrl) {
          let bytes = a.imageBytes;
          if (!bytes) bytes = await dataUrlToBytes(a.imageDataUrl);
          ready.push({ ...a, imageBytes: bytes, imgEl: undefined });
        } else {
          ready.push({ ...a, imgEl: undefined });
        }
      }
      const bytes = await exportAnnotatedPdf(pdfBytes, {}, ready);
      const outName = (fileName || "documento").replace(/\.pdf$/i, "") + "_editado.pdf";
      downloadBytes(bytes, outName);
      notify(t("PDF downloaded", "PDF descargado"));
    } catch (e) {
      console.error(e);
      notify(t(`Export failed: ${e?.message || "error"}`, `Error al exportar: ${e?.message || "error"}`));
    } finally {
      setBusy(false);
    }
  };

  const insertPage = async () => {
    if (!pdfBytes) return;
    setBusy(true);
    try {
      const bytes = await insertBlankPage(pdfBytes, page - 1, "a4");
      await openBytes(bytes, fileName);
      setPage(page + 1);
    } finally {
      setBusy(false);
    }
  };

  const dupPage = async () => {
    if (!pdfBytes) return;
    setBusy(true);
    try {
      const bytes = await duplicatePageInPdf(pdfBytes, page - 1);
      await openBytes(bytes, fileName);
      setPage(page + 1);
    } finally {
      setBusy(false);
    }
  };

  const mergePdfs = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const buffers = pdfBytes ? [pdfBytes] : [];
      for (const f of files) buffers.push(new Uint8Array(await f.arrayBuffer()));
      await openBytes(await mergePdfFiles(buffers), "fusionado.pdf");
    } finally {
      setBusy(false);
    }
  };

  const applyWater = async () => {
    if (!pdfBytes) return;
    setBusy(true);
    try {
      await openBytes(await addWatermarkToBytes(pdfBytes, waterText, { color, opacity: 0.2 }), fileName);
      setShowWater(false);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT") {
        if (e.key === "Escape") setDraftText(null);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        download();
      } else if (e.key === "Delete" && selectedId) {
        commit(anns.filter((a) => a.id !== selectedId));
        setSelectedId(null);
      } else if (e.key === "Escape") {
        setDraftText(null);
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, anns, histIdx, history, pdfBytes]);

  const pageAnns = anns.filter((a) => a.pageIndex === page - 1);
  const textAnns = pageAnns.filter((a) => a.type === "text" || a.type === "textReplace");

  const tools = [
    { id: TOOL.SELECT, icon: <FaMousePointer />, label: t("Select", "Seleccionar") },
    { id: TOOL.TEXT, icon: <FaFont />, label: t("Add text", "Añadir texto"), primary: true },
    { id: TOOL.EDIT, icon: <FaICursor />, label: t("Edit text", "Editar texto"), primary: true },
    { id: TOOL.HIGHLIGHT, icon: <FaHighlighter />, label: t("Highlight", "Resaltar") },
    { id: TOOL.DRAW, icon: <FaPen />, label: t("Draw", "Dibujar") },
    { id: TOOL.RECT, icon: <FaSquare />, label: t("Shape", "Forma") },
    { id: TOOL.NOTE, icon: <FaStickyNote />, label: t("Note", "Nota") },
    { id: TOOL.IMAGE, icon: <FaImage />, label: t("Image", "Imagen") },
    { id: TOOL.SIGN, icon: <FaSignature />, label: t("Sign", "Firmar") },
    { id: TOOL.STAMP, icon: <FaStamp />, label: t("Stamp", "Sello") },
    { id: TOOL.REDACT, icon: <FaBan />, label: t("Redact", "Tapar") },
    { id: TOOL.ERASE, icon: <FaEraser />, label: t("Erase", "Borrar") },
  ];

  /* ---------- WELCOME ---------- */
  if (!pdfDoc) {
    return (
      <div className="pe2-root pe2-welcome">
        <header className="pe2-welcome-bar">
          <Link to="/projects" className="pe2-back">
            <FaArrowLeft /> {t("Projects", "Proyectos")}
          </Link>
          <div>
            <h1>FreePDF Studio</h1>
            <p>{t("Edit PDFs privately in your browser", "Edita PDFs en privado desde tu navegador")}</p>
          </div>
        </header>

        <div
          className={`pe2-drop ${dragOver ? "on" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            openFile(e.dataTransfer.files?.[0]);
          }}
        >
          <FaFileAlt className="pe2-drop-ico" />
          <h2>{t("Drop a PDF here", "Suelta un PDF aquí")}</h2>
          <p>{t("or choose one of the options below", "o elige una de las opciones de abajo")}</p>
          <div className="pe2-welcome-actions">
            <button type="button" className="pe2-btn pe2-btn-main" onClick={() => fileRef.current?.click()}>
              <FaUpload /> {t("Upload PDF", "Subir PDF")}
            </button>
            <button type="button" className="pe2-btn" onClick={() => setShowCreate(true)}>
              <FaFileMedical /> {t("Create blank", "Crear en blanco")}
            </button>
          </div>
        </div>

        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => openFile(e.target.files?.[0])} />
        {showCreate && (
          <Overlay onClose={() => setShowCreate(false)}>
            <h3>{t("New document", "Nuevo documento")}</h3>
            <div className="pe2-chips">
              {Object.entries(PAGE_SIZES).map(([k, v]) => (
                <button type="button" key={k} className={createSize === k ? "on" : ""} onClick={() => setCreateSize(k)}>
                  {v.label}
                </button>
              ))}
            </div>
            <div className="pe2-chips">
              {[
                ["blank", t("Blank", "Blanco")],
                ["lined", t("Lined", "Lineas")],
                ["dots", t("Dots", "Puntos")],
                ["checklist", "Checklist"],
                ["invoice", t("Invoice", "Factura")],
              ].map(([id, label]) => (
                <button type="button" key={id} className={createTpl === id ? "on" : ""} onClick={() => setCreateTpl(id)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="pe2-modal-actions">
              <button type="button" className="pe2-btn" onClick={() => setShowCreate(false)}>{t("Cancel", "Cancelar")}</button>
              <button type="button" className="pe2-btn pe2-btn-main" onClick={createNew} disabled={busy}>
                <FaPlus /> {t("Create", "Crear")}
              </button>
            </div>
          </Overlay>
        )}
        {toast && <div className="pe2-toast">{toast}</div>}
      </div>
    );
  }

  /* ---------- EDITOR ---------- */
  return (
    <div className="pe2-root pe2-app">
      <header className="pe2-top">
        <div className="pe2-top-left">
          <Link to="/projects" className="pe2-icon" title={t("Back", "Volver")}>
            <FaArrowLeft />
          </Link>
          <button type="button" className="pe2-icon" onClick={() => fileRef.current?.click()} title={t("Open", "Abrir")}>
            <FaUpload />
          </button>
          <div className="pe2-title">
            <strong>{fileName}</strong>
            <span>{busy ? t("Working…", "Trabajando…") : t("Saved locally · private", "Local · privado")}</span>
          </div>
        </div>

        <div className="pe2-top-center">
          <button type="button" className="pe2-icon" onClick={undo}><FaUndo /></button>
          <button type="button" className="pe2-icon" onClick={redo}><FaRedo /></button>
          <span className="pe2-sep" />
          <button type="button" className="pe2-icon" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.15).toFixed(2)))}><FaSearchMinus /></button>
          <span className="pe2-pill">{Math.round(zoom * 100)}%</span>
          <button type="button" className="pe2-icon" onClick={() => setZoom((z) => Math.min(2.8, +(z + 0.15).toFixed(2)))}><FaSearchPlus /></button>
          <span className="pe2-sep" />
          <button type="button" className="pe2-icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><FaArrowLeft /></button>
          <span className="pe2-pill">{page} / {numPages}</span>
          <button type="button" className="pe2-icon" disabled={page >= numPages} onClick={() => setPage((p) => p + 1)}><FaArrowRight /></button>
        </div>

        <div className="pe2-top-right">
          <button type="button" className="pe2-btn pe2-btn-main" onClick={download} disabled={busy}>
            <FaDownload /> {t("Download", "Descargar")}
          </button>
        </div>
      </header>

      <div className="pe2-workspace">
        <aside className="pe2-rail">
          {tools.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`pe2-tool ${tool === item.id ? "active" : ""} ${item.primary ? "accent" : ""}`}
              onClick={() => {
                setTool(item.id);
                setDraftText(null);
                setSelectedId(null);
              }}
              title={item.label}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
          <div className="pe2-rail-extra">
            <button type="button" className="pe2-tool" onClick={insertPage} title={t("Blank page", "Página en blanco")}>
              <FaPlus />
              <span>{t("Page", "Página")}</span>
            </button>
            <button type="button" className="pe2-tool" onClick={dupPage} title={t("Duplicate page", "Duplicar")}>
              <FaClone />
              <span>{t("Dup.", "Dup.")}</span>
            </button>
            <button type="button" className="pe2-tool" onClick={() => mergeRef.current?.click()} title={t("Merge", "Fusionar")}>
              <FaObjectGroup />
              <span>{t("Merge", "Fusionar")}</span>
            </button>
            <button type="button" className="pe2-tool" onClick={() => setShowWater(true)} title={t("Watermark", "Marca")}>
              <FaWater />
              <span>{t("Mark", "Marca")}</span>
            </button>
          </div>
        </aside>

        <section className="pe2-center">
          {(tool === TOOL.TEXT || tool === TOOL.EDIT || selected?.type === "text" || selected?.type === "textReplace" || draftText) && (
            <div className="pe2-format">
              <select value={fontFamily} onChange={(e) => { setFontFamily(e.target.value); if (selected) updateSelected({ fontFamily: e.target.value }); }}>
                <option value="helvetica">Helvetica</option>
                <option value="times">Times</option>
                <option value="courier">Courier</option>
              </select>
              <input
                type="number"
                min={8}
                max={96}
                value={draftText?.fontSize || fontSize}
                onChange={(e) => {
                  const v = Number(e.target.value) || 16;
                  setFontSize(v);
                  if (draftText) setDraftText({ ...draftText, fontSize: v });
                  if (selected) updateSelected({ fontSize: v });
                }}
              />
              <button type="button" className={bold ? "on" : ""} onClick={() => { setBold((v) => !v); if (selected) updateSelected({ bold: !bold }); }}><FaBold /></button>
              <button type="button" className={italic ? "on" : ""} onClick={() => { setItalic((v) => !v); if (selected) updateSelected({ italic: !italic }); }}><FaItalic /></button>
              <button type="button" className={underline ? "on" : ""} onClick={() => { setUnderline((v) => !v); if (selected) updateSelected({ underline: !underline }); }}><FaUnderline /></button>
              <span className="pe2-sep" />
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  className={`pe2-swatch ${color === c ? "on" : ""}`}
                  style={{ background: c }}
                  onClick={() => { setColor(c); if (selected) updateSelected({ color: c }); }}
                />
              ))}
              <label className="pe2-swatch pe2-swatch-pick">
                <input type="color" value={color} onChange={(e) => { setColor(e.target.value); if (selected) updateSelected({ color: e.target.value }); }} />
              </label>
              {(tool === TOOL.DRAW || tool === TOOL.RECT) && (
                <label className="pe2-range">
                  {t("Size", "Grosor")}
                  <input type="range" min="1" max="12" value={strokeWidth} onChange={(e) => setStrokeWidth(Number(e.target.value))} />
                </label>
              )}
              {selected && (
                <button type="button" className="pe2-danger" onClick={() => { commit(anns.filter((a) => a.id !== selectedId)); setSelectedId(null); }}>
                  <FaTrash /> {t("Delete", "Eliminar")}
                </button>
              )}
            </div>
          )}

          {tool === TOOL.SIGN && (
            <div className="pe2-edit-toolbar">
              {savedSignature ? (
                <>
                  <span>{t("Saved signature ready", "Firma guardada lista")}</span>
                  <img src={savedSignature} alt="" className="pe2-sig-thumb" />
                  <button
                    type="button"
                    className="on"
                    onClick={() => {
                      setForceNewSignature(true);
                      drawing.current = {
                        pendingSign: true,
                        x: 0.35,
                        y: 0.75,
                        pageIndex: page - 1,
                      };
                      setShowSign(true);
                    }}
                  >
                    {t("Change signature", "Cambiar firma")}
                  </button>
                  <button type="button" onClick={clearSavedSignature}>
                    {t("Remove", "Eliminar")}
                  </button>
                </>
              ) : (
                <span className="pe2-edit-note">
                  {t(
                    "Draw a signature once and save it — next times it will place automatically.",
                    "Dibuja una firma y guárdala — las próximas veces se colocará sola."
                  )}
                </span>
              )}
            </div>
          )}

          {tool === TOOL.EDIT && (
            <div className="pe2-edit-toolbar">
              <span>{t("Detect as", "Detectar como")}</span>
              <button
                type="button"
                className={editGrain === "line" ? "on" : ""}
                onClick={() => setEditGrain("line")}
              >
                {t("Lines", "Líneas")}
                <em>{textLines.length}</em>
              </button>
              <button
                type="button"
                className={editGrain === "block" ? "on" : ""}
                onClick={() => setEditGrain("block")}
              >
                {t("Paragraphs", "Párrafos")}
                <em>{textLayer.length}</em>
              </button>
              <span className="pe2-edit-note">
                {textLines.length
                  ? t("Click a highlighted region to edit", "Haz clic en una zona resaltada para editar")
                  : t("No selectable text found on this page", "No hay texto seleccionable en esta página")}
              </span>
            </div>
          )}

          {hint && tool !== TOOL.EDIT && <div className="pe2-hint">{hint}</div>}

          <div className="pe2-canvas-scroll">
            <div
              className="pe2-stage"
              ref={stageRef}
              style={{ width: pageCss.w || undefined, height: pageCss.h || undefined }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              <canvas ref={canvasRef} className="pe2-pdf" />
              <canvas ref={drawRef} className="pe2-draw" />

              {/* Editable / visible text annotations as HTML */}
              {textAnns.map((a) => (
                <div
                  key={a.id}
                  className={`pe2-text-box ${selectedId === a.id ? "selected" : ""}`}
                  style={{
                    left: `${a.x * 100}%`,
                    top: `${a.y * 100}%`,
                    width: `${Math.max(a.w * 100, 8)}%`,
                    minHeight: `${Math.max(a.h * 100, 2)}%`,
                    color: a.color || "#111",
                    fontSize: a.fontSizeAbsolute
                      ? (a.fontSize || 14) * zoom
                      : (a.fontSize || 16) * ((pageCss.w || 600) / 600),
                    fontWeight: a.bold ? 700 : 400,
                    fontStyle: a.italic ? "italic" : "normal",
                    textDecoration: a.underline ? "underline" : "none",
                    fontFamily:
                      a.fontFamily === "times"
                        ? "Times New Roman, serif"
                        : a.fontFamily === "courier"
                          ? "Courier New, monospace"
                          : "Helvetica, Arial, sans-serif",
                    background: a.type === "textReplace" || a.cover ? "#fff" : "transparent",
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    if (tool === TOOL.ERASE) {
                      commit(anns.filter((x) => x.id !== a.id));
                      return;
                    }
                    setSelectedId(a.id);
                    setTool(TOOL.SELECT);
                    setDraftText({
                      mode: a.type === "textReplace" ? "edit-ann" : "edit-ann",
                      annId: a.id,
                      x: a.x,
                      y: a.y,
                      w: a.w,
                      h: a.h,
                      value: a.text || "",
                      fontSize: a.fontSize || fontSize,
                    });
                  }}
                >
                  {(a.text || "").split("\n").map((line, i) => (
                    <div key={i}>{line || " "}</div>
                  ))}
                </div>
              ))}

              {/* Detected PDF text regions (lines or paragraphs) */}
              {tool === TOOL.EDIT &&
                (editGrain === "block" ? textLayer : textLines).map((it) => (
                  <button
                    type="button"
                    key={it.id}
                    className={`pe2-native-hit ${hoverTextId === it.id ? "hot" : ""} ${
                      draftText?.nativeId === it.id ? "editing" : ""
                    }`}
                    style={{
                      left: `${it.x * 100}%`,
                      top: `${it.y * 100}%`,
                      width: `${Math.max(it.w * 100, 1)}%`,
                      height: `${Math.max(it.h * 100, 1)}%`,
                    }}
                    onPointerEnter={() => setHoverTextId(it.id)}
                    onPointerLeave={() => setHoverTextId(null)}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      openNativeEdit(it);
                    }}
                    title={it.str}
                    aria-label={it.str}
                  >
                    <span className="pe2-native-preview">{it.str}</span>
                  </button>
                ))}

              {/* Inline draft editor */}
              {draftText && (
                <div
                  className={`pe2-draft ${draftText.mode === "edit" ? "pe2-draft-edit" : ""}`}
                  style={{
                    left: `${draftText.x * 100}%`,
                    top: `${draftText.y * 100}%`,
                    width: `${Math.max(
                      (draftText.w || 0.3) * 100,
                      draftText.mode === "edit" ? 22 : 18
                    )}%`,
                    minHeight:
                      draftText.mode === "edit"
                        ? `${Math.max((draftText.h || 0.03) * 100, 3)}%`
                        : undefined,
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="pe2-draft-label">
                    {draftText.mode === "edit"
                      ? t("Editing PDF text", "Editando texto del PDF")
                      : draftText.mode === "note"
                        ? t("Sticky note", "Nota")
                        : t("New text", "Texto nuevo")}
                  </div>
                  <textarea
                    autoFocus
                    rows={
                      draftText.mode === "note"
                        ? 4
                        : Math.min(8, Math.max(2, draftText.lineCount || draftText.value.split("\n").length || 2))
                    }
                    value={draftText.value}
                    placeholder={
                      draftText.mode === "edit"
                        ? t("Edit text…", "Edita el texto…")
                        : draftText.mode === "note"
                          ? t("Write a note…", "Escribe una nota…")
                          : t("Type here…", "Escribe aquí…")
                    }
                    style={{
                      color,
                      fontSize: Math.max(
                        12,
                        draftText.mode === "edit"
                          ? draftText.fontSizeCss || draftText.fontSize * zoom || fontSize
                          : (draftText.fontSize || fontSize) * zoom * 0.9
                      ),
                      fontWeight: bold ? 700 : 400,
                      fontStyle: italic ? "italic" : "normal",
                      textDecoration: underline ? "underline" : "none",
                      fontFamily:
                        fontFamily === "times"
                          ? "Times New Roman, serif"
                          : fontFamily === "courier"
                            ? "Courier New, monospace"
                            : "Helvetica, Arial, sans-serif",
                    }}
                    onChange={(e) => setDraftText({ ...draftText, value: e.target.value })}
                    onKeyDown={(e) => {
                      // Allow Shift+Enter for new lines when editing paragraphs/notes
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        draftText.mode !== "note" &&
                        (draftText.lineCount || 1) <= 1
                      ) {
                        e.preventDefault();
                        confirmDraft();
                      }
                      if (e.key === "Escape") setDraftText(null);
                    }}
                  />
                  <div className="pe2-draft-actions">
                    <button type="button" className="pe2-btn" onClick={() => setDraftText(null)}>
                      <FaTimes /> {t("Cancel", "Cancelar")}
                    </button>
                    <button type="button" className="pe2-btn pe2-btn-main" onClick={confirmDraft}>
                      <FaCheck /> {t("Apply", "Aplicar")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="pe2-pages">
          <div className="pe2-pages-head">{t("Pages", "Páginas")}</div>
          <div className="pe2-thumbs">
            {thumbs.map((src, i) => (
              <button
                type="button"
                key={i}
                className={`pe2-thumb ${page === i + 1 ? "active" : ""}`}
                onClick={() => {
                  setPage(i + 1);
                  setDraftText(null);
                }}
              >
                <img src={src} alt="" />
                <span>{i + 1}</span>
              </button>
            ))}
          </div>
          {selected && (
            <div className="pe2-props">
              <h4>{t("Selected", "Seleccionado")}</h4>
              <p>{selected.type}</p>
              {(selected.type === "text" || selected.type === "textReplace" || selected.type === "note") && (
                <textarea
                  rows={3}
                  value={selected.text || ""}
                  onChange={(e) => updateSelected({ text: e.target.value })}
                />
              )}
            </div>
          )}
        </aside>
      </div>

      <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => openFile(e.target.files?.[0])} />
      <input ref={imgRef} type="file" accept="image/*" hidden onChange={onImagePick} />
      <input ref={mergeRef} type="file" accept="application/pdf" multiple hidden onChange={mergePdfs} />

      {showSign && (
        <Overlay onClose={() => { setShowSign(false); drawing.current = null; setForceNewSignature(false); }}>
          <h3>{t("Signature", "Firma")}</h3>
          {savedSignature && (
            <div className="pe2-sig-saved">
              <img src={savedSignature} alt="" />
              <div className="pe2-sig-saved-actions">
                <button
                  type="button"
                  className="pe2-btn pe2-btn-main"
                  onClick={() => {
                    const d = drawing.current;
                    if (!d?.pendingSign) return;
                    placeSavedSignature(d.x, d.y, d.pageIndex, savedSignature);
                    setShowSign(false);
                  }}
                >
                  <FaCheck /> {t("Use saved", "Usar guardada")}
                </button>
                <button type="button" className="pe2-btn" onClick={clearSavedSignature}>
                  <FaTrash /> {t("Delete saved", "Borrar guardada")}
                </button>
              </div>
            </div>
          )}
          <p className="pe2-sig-help">
            {t("Draw a new signature below", "Dibuja una firma nueva abajo")}
          </p>
          <canvas
            ref={sigRef}
            width={480}
            height={160}
            className="pe2-sig"
            onPointerDown={(e) => {
              sigDraw.current = true;
              const c = sigRef.current;
              const ctx = c.getContext("2d");
              const r = c.getBoundingClientRect();
              ctx.beginPath();
              ctx.moveTo((e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height));
            }}
            onPointerMove={(e) => {
              if (!sigDraw.current) return;
              const c = sigRef.current;
              const ctx = c.getContext("2d");
              const r = c.getBoundingClientRect();
              ctx.lineTo((e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height));
              ctx.stroke();
            }}
            onPointerUp={() => { sigDraw.current = false; }}
          />
          <label className="pe2-check">
            <input
              type="checkbox"
              checked={saveSigAsDefault}
              onChange={(e) => setSaveSigAsDefault(e.target.checked)}
            />
            {t("Save as my default signature", "Guardar como mi firma por defecto")}
          </label>
          <div className="pe2-modal-actions">
            <button type="button" className="pe2-btn" onClick={() => initSig("")}>{t("Clear", "Limpiar")}</button>
            <button type="button" className="pe2-btn pe2-btn-main" onClick={placeSign}>
              <FaSignature /> {t("Place", "Colocar")}
            </button>
          </div>
        </Overlay>
      )}

      {showStamp && (
        <Overlay onClose={() => { setShowStamp(false); drawing.current = null; }}>
          <h3>{t("Stamp", "Sello")}</h3>
          <div className="pe2-chips">
            {["APROBADO", "RECHAZADO", "CONFIDENCIAL", "BORRADOR", "APPROVED", "DRAFT"].map((s) => (
              <button type="button" key={s} className={stampLabel === s ? "on" : ""} onClick={() => setStampLabel(s)}>{s}</button>
            ))}
          </div>
          <input value={stampLabel} onChange={(e) => setStampLabel(e.target.value)} />
          <div className="pe2-modal-actions">
            <button type="button" className="pe2-btn" onClick={() => { setShowStamp(false); drawing.current = null; }}>{t("Cancel", "Cancelar")}</button>
            <button type="button" className="pe2-btn pe2-btn-main" onClick={placeStamp}>{t("Place", "Colocar")}</button>
          </div>
        </Overlay>
      )}

      {showWater && (
        <Overlay onClose={() => setShowWater(false)}>
          <h3>{t("Watermark", "Marca de agua")}</h3>
          <input value={waterText} onChange={(e) => setWaterText(e.target.value)} />
          <div className="pe2-modal-actions">
            <button type="button" className="pe2-btn" onClick={() => setShowWater(false)}>{t("Cancel", "Cancelar")}</button>
            <button type="button" className="pe2-btn pe2-btn-main" onClick={applyWater}>{t("Apply", "Aplicar")}</button>
          </div>
        </Overlay>
      )}

      {toast && <div className="pe2-toast">{toast}</div>}
    </div>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div className="pe2-overlay" onClick={onClose}>
      <div className="pe2-modal" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function clamp01(n) {
  return Math.min(1, Math.max(0, n));
}

function nxIn(v, start, size) {
  return v >= start && v <= start + size;
}

/**
 * Keep image aspect ratio in normalized page coords (0–1).
 * Pixel aspect: (w * pageW) / (h * pageH) === imgW / imgH
 */
function fitImageOnPage(imgW, imgH, pageW, pageH, maxW, maxH) {
  const aspect = Math.max(imgW, 1) / Math.max(imgH, 1);
  const pageAspect = Math.max(pageW, 1) / Math.max(pageH, 1);
  let w = maxW;
  let h = (w * pageAspect) / aspect;
  if (h > maxH) {
    h = maxH;
    w = (h * aspect) / pageAspect;
  }
  return { w, h };
}

async function normalizeSignatureDataUrl(dataUrl) {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const c = document.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(img, 0, 0);
  return cropSignatureToInk(c);
}

/** Trim white padding and make background transparent. */
function cropSignatureToInk(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const ink =
        data[i + 3] > 12 && (data[i] < 248 || data[i + 1] < 248 || data[i + 2] < 248);
      if (ink) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX) return canvas.toDataURL("image/png");

  const pad = 8;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const cw = maxX - minX + 1;
  const ch = maxY - minY + 1;

  const out = document.createElement("canvas");
  out.width = cw;
  out.height = ch;
  const octx = out.getContext("2d");
  octx.drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
  const od = octx.getImageData(0, 0, cw, ch);
  for (let i = 0; i < od.data.length; i += 4) {
    if (od.data[i] > 245 && od.data[i + 1] > 245 && od.data[i + 2] > 245) {
      od.data[i + 3] = 0;
    }
  }
  octx.putImageData(od, 0, 0);
  return out.toDataURL("image/png");
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text).split(/\s+/);
  let line = "";
  let yy = y;
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = word;
      yy += lineHeight;
    } else line = test;
  });
  if (line) ctx.fillText(line, x, yy);
}

function readAsDataURL(file) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

export default PdfEditor;
